import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip
} from '@mui/material';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { NO_DATA_TO_DISPLAY_URL } from '../../utils/constants';
import { useRef, useState } from 'react';

type ColumnConfig<T> = {
    key: keyof T | string;
    label: string;
    render?: (row: T) => React.ReactNode;
    sx?: object;
    skipTooltip?: boolean;
};

type PinnacleDraggableTableProps<T> = {
    columns: ColumnConfig<T>[];
    rows: T[];
    headerColor: string;
    isDragEnabled?: boolean;
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    onReorder: (newRows: T[]) => void;
    setIsReordering?: (value: boolean) => void;
    isReordering?: boolean;
};

export function PinnacleDraggableTable<T>({
    columns,
    rows,
    headerColor,
    isDragEnabled = false,
    onEdit,
    onDelete,
    onReorder,
    setIsReordering,
    isReordering,
}: PinnacleDraggableTableProps<T>) {
    const dragItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

    const handleDragStart = (index: number) => {
        dragItem.current = index;
        setDraggingIndex(index);
    };

    const handleDragEnter = (index: number) => {
        dragOverItem.current = index;
        setHoverIndex(index);
        setIsReordering?.(true);
    };

    const handleDragEnd = () => {
        if (
            dragItem.current === null ||
            dragOverItem.current === null ||
            dragItem.current === dragOverItem.current
        ) {
            dragItem.current = null;
            dragOverItem.current = null;
            setHoverIndex(null);
            setDraggingIndex(null);
            setIsReordering?.(false);
            return;
        }

        const updatedRows = [...rows];
        const draggedRow = updatedRows.splice(dragItem.current, 1)[0];
        updatedRows.splice(dragOverItem.current, 0, draggedRow);

        onReorder(updatedRows);

        dragItem.current = null;
        dragOverItem.current = null;
        setHoverIndex(null);
        setDraggingIndex(null);
    };

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: headerColor }}>
                        {columns.map((col, colIndex) => (
                            <TableCell
                                key={String(col.key)}
                                sx={{ color: 'white', fontWeight: 600, py: 1.5 }}
                            >
                                {col.label.toUpperCase()}
                            </TableCell>
                        ))}
                        {(onEdit || onDelete) && (
                            <TableCell sx={{ color: 'white', fontWeight: 600, py: 1.5 }}>
                                ACTION
                            </TableCell>
                        )}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length + (onEdit || onDelete ? 1 : 0)}
                                sx={{ p: 0 }}
                            >
                                <Box
                                    sx={{
                                        height: '170px',
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        color: '#818181',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={NO_DATA_TO_DISPLAY_URL}
                                        alt="No data"
                                        sx={{
                                            width: '260px',
                                            maxWidth: '100%',
                                            objectFit: 'contain',
                                            mb: 1,
                                        }}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, index) => (
                            <TableRow
                                key={index}
                                onDragEnter={() => isDragEnabled && handleDragEnter(index)}
                                sx={{
                                    backgroundColor:
                                        draggingIndex === index
                                            ? '#E0F2FF'
                                            : hoverIndex === index
                                                ? '#FFFDE7'
                                                : index % 2 === 0
                                                    ? '#F5F5F5'
                                                    : 'white',
                                    opacity: draggingIndex === index ? 0.6 : 1,
                                    border: hoverIndex === index ? '2px dashed #FFD600' : 'none',
                                    transition: 'background-color 0.2s, border 0.2s',
                                }}
                            >
                                {columns.map((col, colIndex) => (
                                    <TableCell key={String(col.key)} sx={col.sx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                            {colIndex === 0 && isDragEnabled && (
                                                <IconButton
                                                    draggable
                                                    onDragStart={() => handleDragStart(index)}
                                                    onDragEnd={handleDragEnd}
                                                    sx={{
                                                        cursor: 'grab',
                                                        opacity: draggingIndex === index ? 0.4 : 1,
                                                        color: '#A8A8A8',
                                                        mr: 1,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    <DragIndicatorIcon />
                                                </IconButton>
                                            )}

                                            <Tooltip
                                                title={col.skipTooltip ? '' : String(row[col.key as keyof T] ?? '')}
                                                arrow
                                            >
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        maxWidth: '100%',
                                                    }}
                                                >
                                                    {col.render
                                                        ? col.render(row)
                                                        : (row[col.key as keyof T] as any)}
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                ))}


                                {(onEdit || onDelete) && (
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                flexWrap: { xs: 'nowrap', sm: 'nowrap' },
                                            }}
                                        >
                                            {onEdit && (
                                                <Button
                                                    variant="contained"
                                                    startIcon={<ModeEditOutlineOutlinedIcon />}
                                                    size="small"
                                                    sx={{ backgroundColor: '#003B71', color: 'white' }}
                                                    onClick={() => onEdit(row)}
                                                    disabled={isReordering}
                                                >
                                                    edit
                                                </Button>
                                            )}
                                            {onDelete && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<DeleteOutlineOutlinedIcon />}
                                                    size="small"
                                                    color="inherit"
                                                    onClick={() => onDelete(row)}
                                                    disabled={isReordering}
                                                    className="btn-white"
                                                >
                                                    delete
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
